import { Box, Heading, Paragraph } from '@zillow/constellation-10';
import { LoadingHeading, LoadingText } from '@zg-rentals/loading-components';

import { FULLSTORY_EXCLUDE } from '../../constants/analytics';
import { LeaseTypes } from '../../models/Types';

type Props = {
  summaryText: string;
  propertyAddress: string;
  personFirstName: string;
  leaseStartDate: string;
  hasColandlord?: boolean;
  leaseType?: LeaseTypes;
  isColandlord?: boolean;
};

export type SummaryTextValue = {
  title: string;
  description: string;
};

export enum SummaryText {
  REVIEW_YOUR_ANSWERS = 'review_your_answers',
  EDIT_SENT_FOR_SIGNATURES = 'edit_sent_for_signatures',
  SENT_DRAFT_TENANT = 'sent_draft_tenant',
  TENANT_OFFLINE_SIGNING = 'tenant_offline_signing',
  DOWNLOAD_FOR_OFFLINE_SIGNING = 'download_for_offline_signing',
  SIGNATURES_IN_PROGRESS = 'signatures_in_progress', // Sent for online signing (Landlord and co-landlord)
  TENANT_WAITING_FOR_OTHERS = 'tenant_waiting_for_others',
  TENANT_VIEW_AND_SIGN = 'tenant_view_and_sign',
  TENANTS_HAVE_SIGNED = 'tenants_have_signed',
  TENANT_SUMMARY = 'tenant_summary',
  COLANDLORD_COUNTERSIGNING = 'colandlord_countersigning', // Sent to co-landlord, before they signed
  COLANDLORD_SIGNED = 'colandlord_signed', // Sent to co-landlord, after they signed
  SIGNING_CANCELLED = 'signing_cancelled', // Signing canceled (Landlord and co-landlord)
  SIGNING_CANCELLED_TENANT = 'signing_cancelled_tenant',
  SIGNING_COMPLETE = 'signing_complete',
  SIGNING_COMPLETE_TENANT = 'signing_complete_tenant',
  LEASE_ACTIVE = 'lease_active',
  LEASE_ACTIVE_TENANT = 'lease_active_tenant',
  LEASE_EXPIRED = 'lease_expired',
  LEASE_TERMINATED = 'lease_terminated',
  LEASE_EXPIRED_TENANT = 'lease_expired_tenant',
  LEASE_TERMINATED_TENANT = 'lease_terminated_tenant',
  COMPLETE_EACH_SECTION = 'complete_each_section',
  NEARLY_READY_TO_BE_SIGNED = 'nearly_ready_to_be_signed',
  LEASE_EXTENSION_REVIEW_YOUR_ANSWERS = 'lease_extension_review_your_answers',
  LEASE_EXTENSION_EDIT_SENT_FOR_SIGNATURES = 'lease_extension_edit_sent_for_signatures',
  LEASE_EXTENSION_SENT_DRAFT_TENANT = 'lease_extension_sent_draft_tenant',
  LEASE_EXTENSION_SIGNATURES_IN_PROGRESS = 'lease_extension_signatures_in_progress',
  LEASE_EXTENSION_TENANT_WAITING_FOR_OTHERS = 'lease_extension_tenant_waiting_for_others',
  LEASE_EXTENSION_TENANT_VIEW_AND_SIGN = 'lease_extension_tenant_view_and_sign',
  LEASE_EXTENSION_TENANTS_HAVE_SIGNED = 'lease_extension_tenants_have_signed',
  LEASE_EXTENSION_TENANT_SUMMARY = 'lease_extension_tenant_summary',
  LEASE_EXTENSION_COLANDLORD_COUNTERSIGNING = 'lease_extension_colandlord_countersigning',
  LEASE_EXTENSION_COLANDLORD_SIGNED = 'lease_extension_colandlord_signed',
  LEASE_EXTENSION_SIGNING_CANCELLED = 'lease_extension_signing_cancelled',
  LEASE_EXTENSION_SIGNING_CANCELLED_TENANT = 'lease_extension_signing_cancelled_tenant',
  LEASE_EXTENSION_SIGNING_COMPLETE = 'lease_extension_signing_complete',
  LEASE_EXTENSION_SIGNING_COMPLETE_TENANT = 'lease_extension_signing_complete_tenant',
  LEASE_EXTENSION_ACTIVE = 'lease_extension_active',
  LEASE_EXTENSION_ACTIVE_TENANT = 'lease_extension_active_tenant',
  LEASE_EXTENSION_EXPIRED = 'lease_extension_expired',
  LEASE_EXTENSION_EXPIRED_TENANT = 'lease_extension_expired_tenant',
  LEASE_EXTENSION_COMPLETE_EACH_SECTION = 'lease_extension_complete_each_section',
  LEASE_EXTENSION_NEARLY_READY_TO_BE_SIGNED = 'lease_extension_nearly_ready_to_be_signed',
}

export const summaryTextMap = {
  [SummaryText.REVIEW_YOUR_ANSWERS]: () => ({
    title: 'Review your answers',
    description:
      'Review, change or complete your answers, then preview your full lease. ' +
      'Your full lease package will include all the legal language, local requirements and additional documents.',
  }),
  [SummaryText.EDIT_SENT_FOR_SIGNATURES]: () => ({
    title: 'Editing a lease that has been sent for signatures',
    description:
      'You are editing a lease agreement that has already been shared with your renters. ' +
      'They can still access the old lease, and they know you are updating it. However, ' +
      'your renters cannot view the changes until you request signatures again. This will ' +
      'restart the signing process and share out your updated lease.',
  }),
  [SummaryText.SENT_DRAFT_TENANT]: (address, firstName) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, ` +
      `your landlord is currently making changes to the lease agreement. ` +
      `In the meantime, you can still review the previous lease details & full lease here.`,
  }),
  [SummaryText.TENANT_OFFLINE_SIGNING]: (address, firstName) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, all your lease terms are here. ` +
      `You can review all the details, and download the draft lease document for your review. ` +
      `Your landlord will bring the full lease for you to sign in-person.`,
  }),
  [SummaryText.DOWNLOAD_FOR_OFFLINE_SIGNING]: () => ({
    title: 'Download the lease for offline signing',
    description:
      'You can review your list of terms, or view the full lease and download it for offline signing. ' +
      'Your full lease includes all the legal language, local requirements, and additional documents.',
  }),
  [SummaryText.SIGNATURES_IN_PROGRESS]: () => ({
    title: 'Signatures are in progress',
    description:
      'Your lease has been sent for electronic signing to your tenants to sign first. You’ll be ' +
      'able to sign the full lease once all tenants have signed. In the meantime, you can review your ' +
      'full lease here. Your full lease package includes all the legal language, local requirements, ' +
      'and additional documents.',
  }),
  [SummaryText.TENANT_WAITING_FOR_OTHERS]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, you’ve signed the lease already, ` +
      `but we are still waiting for signatures from others. ` +
      `In the meantime, you can review all the details and view the full lease document. ` +
      `We’ll keep you posted on the signing progress.`,
  }),
  [SummaryText.TENANT_VIEW_AND_SIGN]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description: `Hi${firstName ? ` ${firstName}` : ''}, all your lease terms are here.`,
  }),
  [SummaryText.TENANTS_HAVE_SIGNED]: (
    address: string,
    firstName: string,
    startDate: string,
    hasColandlord: boolean,
  ) => ({
    title: hasColandlord
      ? 'All your tenants and the co-landlord have signed. It’s your turn.'
      : 'All tenants have signed. It’s your turn.',
    description:
      'It’s your turn to sign and finalize the lease. You can review your list of terms ' +
      'or view the full lease to sign it. Your full lease includes all the legal language, ' +
      'local requirements, and additional documents.',
  }),
  [SummaryText.TENANT_SUMMARY]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, you’ve signed the lease already, ` +
      `but we are still waiting for signatures from others. ` +
      `In the meantime, you can review all the details and view the full lease document. ` +
      `We’ll keep you posted on the signing progress.`,
  }),
  [SummaryText.COLANDLORD_COUNTERSIGNING]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, here are all your lease terms. ` +
      `You can review the summary or view the full lease (and sign it).`,
  }),
  [SummaryText.COLANDLORD_SIGNED]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, here are all your lease terms. ` +
      `You can review the summary or view the full lease.`,
  }),
  [SummaryText.SIGNING_CANCELLED]: (
    address: string,
    firstName: string,
    startDate: string,
    hasColandlord: boolean,
    leaseType: LeaseTypes,
    isColandlord: boolean,
  ) => ({
    title: 'Your lease was canceled',
    description: isColandlord
      ? 'If you still want to move forward with the lease, let your co-landlord make any other needed changes on their account, ' +
        'then have them request signatures again.'
      : 'If you still want to move forward with the lease, you can make any other needed changes here, ' +
        'then request signatures again.',
  }),
  [SummaryText.SIGNING_CANCELLED_TENANT]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, signing has been canceled. ` +
      `In the meantime, you can still review the previous lease details & full lease here.`,
  }),
  [SummaryText.SIGNING_COMPLETE]: () => ({
    title: 'Congratulations',
    description:
      'All parties have signed. You can no longer make changes to this lease agreement, ' +
      'but you can still review your list of terms, or view and download the signed lease here.',
  }),
  [SummaryText.SIGNING_COMPLETE_TENANT]: (
    address: string,
    firstName: string,
    startDate: string,
    hasColandlord: boolean,
    leaseType: LeaseTypes,
  ) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      leaseType === LeaseTypes.SIGNED_SELF_UPLOAD
        ? `Your lease is active on ${startDate}. You can always come back and view the lease here, and ask questions to your landlord.`
        : `Congratulations${firstName ? ` ${firstName}` : ''}, everybody has signed ` +
          `Your lease becomes active ${startDate ? ` on ${startDate}` : `soon`}. ` +
          `You can always come back and view the lease here, and ask questions to your landlord.`,
  }),
  [SummaryText.LEASE_ACTIVE]: () => ({
    title: 'Your lease is active',
    description:
      'All parties have signed. You can no longer make changes to this lease agreement, ' +
      'but you can still review your list of terms, or view and download the signed lease here.',
  }),
  [SummaryText.LEASE_ACTIVE_TENANT]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Congratulations${firstName ? ` ${firstName}` : ''}, your lease is now active. ` +
      `You can always come back and view or download the lease here, and ask questions to your landlord.`,
  }),
  [SummaryText.LEASE_EXPIRED]: () => ({
    title: 'This lease has expired',
    description:
      'Review and then preview your expired full lease here. ' +
      'Your full lease package will include all the legal language, local requirements, and additional documents.',
  }),
  [SummaryText.LEASE_TERMINATED]: () => ({
    title: 'This lease has been canceled',
    description:
      'Review and then preview your canceled full lease here. ' +
      'Your full lease package will include all the legal language, local requirements, and additional documents.',
  }),
  [SummaryText.LEASE_EXPIRED_TENANT]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, your lease has expired. ` +
      `You can still access your expired lease here, and review key terms.`,
  }),
  [SummaryText.LEASE_TERMINATED_TENANT]: (address: string, firstName: string) => ({
    title: `Lease summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, your lease has been canceled by your landlord. ` +
      `You can still access your canceled lease here, and review key terms.`,
  }),
  [SummaryText.LEASE_EXTENSION_REVIEW_YOUR_ANSWERS]: () => ({
    title: 'Review your answers',
    description: 'Review, change or complete your answers, then preview your full lease renewal agreement.',
  }),
  [SummaryText.LEASE_EXTENSION_EDIT_SENT_FOR_SIGNATURES]: () => ({
    title: 'Editing a lease renewal agreement that has been sent for signatures',
    description:
      'You are editing a lease agreement that has already been shared with your renters. ' +
      'They can still access the old lease renewal agreement, and they know you are updating it. However, ' +
      'your renters cannot view the changes until you request signatures again. This will ' +
      'restart the signing process and share out your updated lease renewal agreement.',
  }),
  [SummaryText.LEASE_EXTENSION_SENT_DRAFT_TENANT]: (address, firstName) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, ` +
      `your landlord is currently making changes to the lease renewal agreement. ` +
      `In the meantime, you can still review the previous lease renewal details & full lease renewal agreement here.`,
  }),
  [SummaryText.LEASE_EXTENSION_SIGNATURES_IN_PROGRESS]: () => ({
    title: 'Signatures are in progress',
    description:
      'Your lease renewal agreement has been sent for electronic signing to your tenants to sign first. You’ll be ' +
      'able to sign the full lease renewal once all tenants have signed. In the meantime, you can review your ' +
      'full lease renewal agreement here.',
  }),
  [SummaryText.LEASE_EXTENSION_TENANT_WAITING_FOR_OTHERS]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, you’ve signed the lease renewal agreement already, ` +
      `but we are still waiting for signatures from others. ` +
      `In the meantime, you can review all the details and view the full lease renewal agreement document. ` +
      `We’ll keep you posted on the signing progress.`,
  }),
  [SummaryText.LEASE_EXTENSION_TENANT_VIEW_AND_SIGN]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description: `Hi${firstName ? ` ${firstName}` : ''}, all your lease renewal agreement terms are here.`,
  }),
  [SummaryText.LEASE_EXTENSION_TENANTS_HAVE_SIGNED]: (
    address: string,
    firstName: string,
    startDate: string,
    hasColandlord: boolean,
  ) => ({
    title: hasColandlord
      ? 'All your tenants and the co-landlord have signed. It’s your turn.'
      : 'All tenants have signed. It’s your turn.',
    description:
      'It’s your turn to sign and finalize the lease renewal agreement. You can review your list of terms ' +
      'or view the full lease renewal agreement to sign it.',
  }),
  [SummaryText.LEASE_EXTENSION_TENANT_SUMMARY]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, you’ve signed the lease renewal agreement already, ` +
      `but we are still waiting for signatures from others. ` +
      `In the meantime, you can review all the details and view the full lease renewal agreement document. ` +
      `We’ll keep you posted on the signing progress.`,
  }),
  [SummaryText.LEASE_EXTENSION_COLANDLORD_COUNTERSIGNING]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, here are all your lease renewal agreement terms. ` +
      `You can review the summary or view the full lease renewal agreement (and sign it).`,
  }),
  [SummaryText.LEASE_EXTENSION_COLANDLORD_SIGNED]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, here are all your lease renewal agreement terms. ` +
      `You can review the summary or view the full lease renewal agreement.`,
  }),
  [SummaryText.LEASE_EXTENSION_SIGNING_CANCELLED]: (
    address: string,
    firstName: string,
    startDate: string,
    hasColandlord: boolean,
    leaseType: LeaseTypes,
    isColandlord: boolean,
  ) => ({
    title: 'Your lease renewal agreement was canceled',
    description: isColandlord
      ? 'If you still want to move forward with the lease renewal agreement, let your co-landlord make any other needed changes on their account, ' +
        'then have them request signatures again.'
      : 'If you still want to move forward with the lease renewal agreement, you can make any other needed changes here, ' +
        'then request signatures again.',
  }),
  [SummaryText.LEASE_EXTENSION_SIGNING_CANCELLED_TENANT]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, signing has been canceled. ` +
      `In the meantime, you can still review the previous lease renewal agreement details & full lease renewal agreement here.`,
  }),
  [SummaryText.LEASE_EXTENSION_SIGNING_COMPLETE]: () => ({
    title: 'Congratulations',
    description:
      'All parties have signed. You can no longer make changes to this lease renewal agreement, ' +
      'but you can still review your list of terms, or view and download the signed lease renewal agreement here.',
  }),
  [SummaryText.LEASE_EXTENSION_SIGNING_COMPLETE_TENANT]: (address: string, firstName: string, startDate: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Congratulations${firstName ? ` ${firstName}` : ''}, everybody has signed. ` +
      `Your lease renewal agreement becomes active ${startDate ? ` on ${startDate}` : `soon`}. ` +
      `You can always come back and view the lease renewal agreement here, and ask questions to your landlord.`,
  }),
  [SummaryText.LEASE_EXTENSION_ACTIVE]: () => ({
    title: 'Your lease renewal agreement is active',
    description:
      'All parties have signed. You can no longer make changes to this lease renewal agreement, ' +
      'but you can still review your list of terms, or view and download the signed lease renewal agreement here.',
  }),
  [SummaryText.LEASE_EXTENSION_ACTIVE_TENANT]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Congratulations${firstName ? ` ${firstName}` : ''}, your lease renewal agreement is now active. ` +
      `You can always come back and view or download the lease renewal agreement here, and ask questions to your landlord.`,
  }),
  [SummaryText.LEASE_EXTENSION_EXPIRED]: () => ({
    title: 'This lease renewal agreement has expired',
    description: 'Review and then preview your expired full lease renewal agreement here.',
  }),
  [SummaryText.LEASE_EXTENSION_EXPIRED_TENANT]: (address: string, firstName: string) => ({
    title: `Lease renewal agreement summary${address ? ` - ${address}` : ''}`,
    description:
      `Hi${firstName ? ` ${firstName}` : ''}, your lease renewal agreement has expired. ` +
      `You can still access your expired lease renewal agreement here, and review key terms.`,
  }),
};

export default function SummaryTitle({
  summaryText,
  propertyAddress,
  personFirstName,
  leaseStartDate,
  hasColandlord,
  leaseType,
  isColandlord,
}: Props): JSX.Element {
  const summaryTextValue: SummaryTextValue = summaryTextMap[summaryText]
    ? summaryTextMap[summaryText](
        propertyAddress,
        personFirstName,
        leaseStartDate,
        hasColandlord,
        leaseType,
        isColandlord,
      )
    : { title: '', description: '' };

  // Show loading state when summaryText is not available
  const isLoading = !summaryText || (!summaryTextValue.title && !summaryTextValue.description);

  if (isLoading) {
    return (
      <div className="summary-title">
        <LoadingHeading isLoading level={3} loadingWidth="40%">
          Loading
        </LoadingHeading>
        <Box css={{ marginBottom: 'tighter' }} />
        <LoadingText isLoading loadingWidth="70%">
          Loading
        </LoadingText>
        <Box css={{ marginBottom: 'tight' }} />
      </div>
    );
  }

  return (
    <div className="summary-title">
      <Heading
        level={2}
        data-test="title"
        className={FULLSTORY_EXCLUDE}
        css={{ marginBottom: 'tighter', textStyle: 'responsive.heading-sm' }}
      >
        {summaryTextValue.title}
      </Heading>
      <Paragraph data-test="description" css={{ marginBottom: 'tight' }}>
        {summaryTextValue.description}
      </Paragraph>
    </div>
  );
}
